import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { BookingProgress } from '../components/booking/BookingProgress';
import { BookingStep } from '../components/booking/BookingStep';
import { BookingTypeSelector } from '../components/booking/BookingTypeSelector';
import { PropertyPicker } from '../components/booking/PropertyPicker';
import { DateRangeCalendar } from '../components/booking/DateRangeCalendar';
import { GuestCounter } from '../components/booking/GuestCounter';
import { PhotoshootScheduler } from '../components/booking/PhotoshootScheduler';
import { GuestDetailsForm } from '../components/booking/GuestDetailsForm';
import { StayOptions } from '../components/booking/StayOptions';
import { ReviewSummary } from '../components/booking/ReviewSummary';
import { AvailabilityNotice } from '../components/booking/AvailabilityNotice';
import { PriceBreakdown } from '../components/booking/PriceBreakdown';
import { SubmitPanel } from '../components/booking/SubmitPanel';
import { StepNavigation } from '../components/booking/StepNavigation';
import { PaymentReturnNotice } from '../components/booking/PaymentReturnNotice';
import { BOOKING_STEPS, TOTAL_STEPS, useBookingForm } from '../hooks/useBookingForm';
import { stayTimes } from '../data/units';
const EASE = [0.23, 1, 0.32, 1] as const;
const stepTitles: Record<number, {
  title: string;
  intro: string;
}> = {
  1: {
    title: 'What would you like to book?',
    intro: 'Choose an overnight apartment stay or a photoshoot session.'
  },
  2: {
    title: 'Choose your details',
    intro: 'Select your apartment, dates and number of guests.'
  },
  3: {
    title: 'Your information',
    intro: 'We’ll use these details to confirm your booking.'
  },
  4: {
    title: 'Extras & review',
    intro: 'Add optional requests and review your charges before submitting.'
  },
  5: {
    title: 'Confirm & pay',
    intro: 'We’ll create your booking, then take you to Paystack to pay securely.'
  }
};
export function BookingPage() {
  const form = useBookingForm();
  const {
    step,
    isStay
  } = form;
  const heading = step === 2 && !isStay ? {
    title: 'Choose your session',
    intro: 'Pick a date, start time and the number of people on set.'
  } : stepTitles[step];
  const showSummary = step > 1 && !form.result;
  const estimateReady = isStay ? form.estimate.nights > 0 : Boolean(form.shootDate && form.startTime);
  return <>
      <PageHeader eyebrow="Booking" title="Reserve your stay" intro={`Five simple steps. Check-in ${stayTimes.checkIn} · checkout ${stayTimes.checkOut}.`} data-id="element-473" data-mp="2m4fwhqv2scoo" />

      <section className="bg-ivory-100" data-id="element-474" data-mp="2ux12s7hisqwm">
        <div id="booking-wizard" className="mx-auto max-w-[1240px] scroll-mt-24 px-5 py-12 sm:px-8 lg:px-12 lg:py-16" data-id="element-475" data-mp="2v05ctlho2gv7">
          <PaymentReturnNotice data-id="element-476" data-mp="34w8opj2bltvc" />
          <div className="mx-auto max-w-3xl lg:max-w-none" data-id="element-477" data-mp="34xa2vwemuwha">
            <BookingProgress steps={BOOKING_STEPS} current={step} furthest={form.furthest} onSelect={form.goTo} data-id="element-478" data-mp="2zxnovkqzdc7f" />
          </div>

          <div className={`mt-10 grid gap-10 ${showSummary ? 'lg:grid-cols-[1.55fr_1fr] lg:gap-12' : 'mx-auto max-w-3xl'}`} data-id="element-479" data-mp="3jk8rljm3vcsb">
            <div className="overflow-hidden rounded-sm bg-ivory-50 p-6 shadow-soft ring-1 ring-forest-700/5 sm:p-10" data-id="element-480" data-mp="3ohipzorh2ysa">
              <AnimatePresence mode="wait" initial={false} custom={form.direction} data-id="element-481" data-mp="39ndun55hdkg5">
                <motion.div key={step} custom={form.direction} initial={{
                opacity: 0,
                x: form.direction * 24
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: form.direction * -24
              }} transition={{
                duration: 0.24,
                ease: EASE
              }} data-id="element-482" data-mp="3ei9ped23qlwy">
                  {!form.result && <header className="mb-8" data-id="element-483" data-mp="2vtwapj8vu5pl">
                      <p className="text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-484" data-mp="2htbtgrim8hy7">
                        Step {step} of {TOTAL_STEPS}
                      </p>
                      <h2 className="mt-2 font-display text-2xl text-forest-700 sm:text-[1.9rem]" data-id="element-485" data-mp="2lx9r0xsalkp5">{heading.title}</h2>
                      <p className="mt-2 text-[14px] text-muted" data-id="element-486" data-mp="29tflvzjxt9sd">{heading.intro}</p>
                    </header>}

                  {step === 1 && <BookingTypeSelector value={form.bookingType} onChange={form.changeType} data-id="element-487" data-mp="250uu13kld6rh" />}

                  {step === 2 && isStay && <div className="space-y-10" data-id="element-488" data-mp="1w1r8zu1v6hac">
                      <BookingStep title="Apartment" data-id="element-489" data-mp="1w2iw0gheb6p0">
                        <PropertyPicker value={form.propertyId} onChange={form.changeProperty} data-id="element-490" data-mp="1r2xrzrndc85f" />
                      </BookingStep>
                      <BookingStep title="Dates" hint="Tap your check-in date, then your check-out date." error={form.errors.dates} data-id="element-491" data-mp="1m4miji0zjznq">
                        <DateRangeCalendar start={form.checkIn} end={form.checkOut} onChange={form.changeDates} data-id="element-492" data-mp="1h6l274uy2efy" />
                        <AvailabilityNotice type="apartment" data-id="element-493" data-mp="3gf8c1rsnyrzl" />
                      </BookingStep>
                      <BookingStep title="Guests" error={form.errors.guests} data-id="element-494" data-mp="3bgor9upt3m8t">
                        <GuestCounter value={form.guests} max={form.maxGuests} onChange={form.changeGuests} data-id="element-495" data-mp="3jz9wzpot8qd1" />
                      </BookingStep>
                    </div>}

                  {step === 2 && !isStay && <div className="space-y-10" data-id="element-496" data-mp="1l9afn1qah8r">
                      <BookingStep title="Date & time" hint="Sessions last 3 hours — the end time is calculated for you." error={form.errors.shoot} data-id="element-497" data-mp="1l10a2wzpq19">
                        <PhotoshootScheduler date={form.shootDate} startTime={form.startTime} endTime={form.endTime} onDateChange={form.changeShootDate} onStartTimeChange={form.changeStartTime} data-id="element-498" data-mp="351zjybwircf7" />
                        <AvailabilityNotice type="photoshoot" data-id="element-499" data-mp="pall6rcpexd6" />
                      </BookingStep>
                      <BookingStep title="People on set" error={form.errors.guests} data-id="element-500" data-mp="u8n3ha2b2snl">
                        <GuestCounter value={form.guests} max={form.maxGuests} label="Number of guests" hint={`Including talent and crew · maximum ${form.maxGuests}`} onChange={form.changeGuests} data-id="element-501" data-mp="4krb7sdtv5bt" />
                      </BookingStep>
                    </div>}

                  {step === 3 && <GuestDetailsForm value={form.guest} errors={{
                  fullName: form.errors.fullName,
                  email: form.errors.email,
                  phone: form.errors.phone
                }} onChange={form.updateGuest} data-id="element-502" data-mp="3d2e0pt68vd41" />}

                  {step === 4 && <div className="space-y-10" data-id="element-503" data-mp="2z96941i5yhmc">
                      {isStay && <StayOptions earlyCheckIn={form.earlyCheckIn} lateCheckout={form.lateCheckout} onToggleEarly={form.toggleEarly} onToggleLate={form.toggleLate} data-id="element-504" data-mp="2uabgy8yb3gpk" />}
                      <BookingStep title="Review" error={form.errors.terms} data-id="element-505" data-mp="zwqwjuu8lnb5">
                        <ReviewSummary form={form} data-id="element-506" data-mp="14vajnykkh553" />
                        <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-sm p-1 text-[13px] leading-relaxed text-ink" data-id="element-507" data-mp="14rwekikiqhty">
                          <input type="checkbox" checked={form.acceptedTerms} onChange={form.toggleTerms} className="mt-0.5 h-4 w-4 shrink-0 accent-forest-700" data-id="element-508" data-mp="1xj6fl12bat0t" />
                          <span data-id="element-509" data-mp="83qf0a2gefmj">
                            I accept the{' '}
                            <Link to="/about" className="text-forest-600 underline underline-offset-2 hover:text-forest-800" data-id="element-510" data-mp="c5bs9wiwgvx9">
                              house rules
                            </Link>
                            , including the ₦100,000 refundable caution fee, and understand early check-in and late checkout fees
                            apply only if approved.
                          </span>
                        </label>
                      </BookingStep>
                    </div>}

                  {step === 5 && <SubmitPanel form={form} data-id="element-511" data-mp="1mj4jv3fr9cu0" />}
                </motion.div>
              </AnimatePresence>

              {!form.result && form.submitStatus === 'idle' && <StepNavigation step={step} totalSteps={TOTAL_STEPS} estimateTotal={step > 1 && estimateReady ? form.estimate.total : null} onBack={form.back} onNext={form.next} data-id="element-512" data-mp="1dqj29qnv1c6r" />}
            </div>

            {showSummary && <div className="hidden lg:block" data-id="element-513" data-mp="21etz8iqfqrpr">
                <PriceBreakdown bookingType={form.bookingType} property={form.property} checkIn={form.checkIn} checkOut={form.checkOut} shootDate={form.shootDate} startTime={form.startTime} endTime={form.endTime} guests={form.guests} estimate={form.estimate} data-id="element-514" data-mp="28suw8kgjo9i2" />
              </div>}
          </div>
        </div>
      </section>
    </>;
}